import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { Users, Wallet, BarChart2, Inbox, CheckCircle2, ChevronDown, ChevronUp } from 'lucide-react';

const fmt = (n) => Number(n || 0).toLocaleString('th-TH', { minimumFractionDigits: 0 });

export default function Home() {
  const { isAdmin, user } = useAuth();
  const [debtors, setDebtors] = useState([]);
  const [myDebts, setMyDebts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [publicSummary, setPublicSummary] = useState([]);
  const [expandedAdmin, setExpandedAdmin] = useState(null);

  const formatDateLao = (date) => {
    const days = ['ວັນອາທິດ','ວັນຈັນ','ວັນອັງຄານ','ວັນພຸດ','ວັນພະຫັດ','ວັນສຸກ','ວັນເສົາ'];
    const months = ['ມັງກອນ','ກຸມພາ','ມີນາ','ເມສາ','ພຶດສະພາ','ມິຖຸນາ','ກໍລະກົດ','ສິງຫາ','ກັນຍາ','ຕຸລາ','ພະຈິກ','ທັນວາ'];
    return `${days[date.getDay()]} ທີ ${date.getDate()} ${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  useEffect(() => {
    const load = async () => {
      try {
        if (user) {
          const [d] = await Promise.all([api.get('/debtors')]);
          setDebtors(d.data);
          if (isAdmin) {
            const m = await api.get('/mydebt');
            setMyDebts(m.data);
          }
        } else {
          // Not logged in — load public summary grouped by admin
          const res = await api.get('/public/summary');
          setPublicSummary(res.data);
        }
      } catch { }
      setLoading(false);
    };
    load();
  }, [isAdmin, user]);

  const totalOwe = debtors.reduce((s, d) => s + (d.totalDebt || 0), 0);
  const totalMyDebt = myDebts.reduce((s, d) => s + (d.totalDebt || 0), 0);
  const activeDebtors = debtors.filter(d => (d.totalDebt || 0) > 0);

  if (loading) return (
    <div className="flex items-center justify-center pt-20">
      <div className="w-8 h-8 border-2 border-dark border-t-transparent rounded-full animate-spin" />
    </div>
  );

  // ==================== PUBLIC VIEW (not logged in) ====================
  if (!user) {
    return (
      <div className="space-y-4 animate-fade-in">
        <div className="pt-2">
          <h1 className="text-xl font-bold text-dark">DebtBook</h1>
          <p className="text-muted text-sm">{formatDateLao(new Date())}</p>
        </div>

        <div className="card bg-dark/5 border-dark/10 text-center py-3">
          <p className="text-muted text-xs">ເຂົ້າສູ່ລະບົບເພື່ອຈັດການຂໍ້ມູນ</p>
          <Link to="/login" className="inline-block mt-2 btn-primary text-xs py-2 px-5">ເຂົ້າສູ່ລະບົບ</Link>
        </div>

        <div>
          <h2 className="font-bold text-dark text-sm mb-3">ຂໍ້ມູນລູກໜີ້ທັງໝົດ</h2>

          {publicSummary.length === 0 ? (
            <div className="card text-center py-8">
              <div className="flex justify-center mb-2 text-muted/40"><Inbox size={36} /></div>
              <p className="text-muted text-sm">ຍັງບໍ່ມີຂໍ້ມູນ</p>
            </div>
          ) : (
            <div className="space-y-3">
              {publicSummary.map((admin) => (
                <div key={admin.adminId} className="card space-y-3 animate-fade-in">
                  {/* Admin header */}
                  <button
                    onClick={() => setExpandedAdmin(expandedAdmin === admin.adminId ? null : admin.adminId)}
                    className="w-full flex items-center justify-between"
                  >
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-dark rounded-xl flex items-center justify-center flex-shrink-0">
                        <span className="text-sand font-bold text-sm">{admin.username[0].toUpperCase()}</span>
                      </div>
                      <div className="text-left">
                        <p className="font-bold text-dark text-sm">ຊື່ເຈົ້າໜີ້: {admin.username}</p>
                        <p className="text-xs text-muted">{admin.activeDebtors} ລາຍຄ້າງຊຳລະ / {admin.totalDebtors} ລາຍທັງໝົດ</p>
                      </div>
                    </div>
                    <div className="text-right flex items-center gap-2">
                      <div>
                        <p className={`font-bold text-sm ${admin.totalDebt > 0 ? 'text-red-500' : 'text-green-500'}`}>
                          {fmt(admin.totalDebt)} KIP
                        </p>
                        <p className="text-xs text-muted">ຍອດລວມ</p>
                      </div>
                      {expandedAdmin === admin.adminId
                        ? <ChevronUp size={16} className="text-muted" />
                        : <ChevronDown size={16} className="text-muted" />}
                    </div>
                  </button>

                  {/* Debtor list (expanded) */}
                  {expandedAdmin === admin.adminId && (
                    <div className="space-y-1.5 border-t border-dark/10 pt-3">
                      {admin.debtors.map((d) => (
                        <Link key={d._id} to={`/debtors/${d._id}`}
                          className="flex items-center justify-between py-2 px-3 rounded-xl bg-dark/5 active:scale-[0.98] transition-all">
                          <div className="flex items-center gap-2.5">
                            <div className="w-7 h-7 bg-dark/10 rounded-lg flex items-center justify-center">
                              <span className="text-dark font-bold text-xs">{(d.name || '?')[0]}</span>
                            </div>
                            <div>
                              <p className="font-medium text-dark text-sm">{d.name}</p>
                              <p className="text-xs text-muted">ຂອງ {admin.username}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5">
                            {(d.totalDebt || 0) <= 0
                              ? <CheckCircle2 size={13} className="text-green-500" />
                              : null}
                            <p className={`font-bold text-xs ${(d.totalDebt || 0) > 0 ? 'text-red-500' : 'text-green-500'}`}>
                              {fmt(d.totalDebt)} KIP
                            </p>
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ==================== LOGGED IN VIEW ====================
  return (
    <div className="space-y-4 animate-fade-in">
      {/* Welcome */}
      <div className="pt-2">
        <h1 className="text-xl font-bold text-dark">
          {`ສະບາຍດີ, ${user.username}`}
        </h1>
        <p className="text-muted text-sm">{formatDateLao(new Date())}</p>
      </div>

      {/* Summary cards */}
      <div className={`grid ${isAdmin ? 'grid-cols-2' : 'grid-cols-1'} gap-3`}>
        <div className="card bg-gradient-to-br from-dark to-dark/80 border-0">
          <p className="text-cream/60 text-xs mb-1">ລູກໜີ້ທັງໝົດ</p>
          <p className="text-2xl font-bold text-sand">{fmt(totalOwe)} KIP</p>
          <p className="text-cream/40 text-xs mt-1">{activeDebtors.length} ລາຍທີ່ຍັງຄ້າງ</p>
        </div>
        {isAdmin && (
          <div className="card bg-gradient-to-br from-red-900/80 to-red-800/60 border-red-700/30">
            <p className="text-red-200/60 text-xs mb-1">ໜີ້ທີ່ເຮົາຕິດ</p>
            <p className="text-2xl font-bold text-red-300">{fmt(totalMyDebt)} KIP</p>
            <p className="text-red-200/40 text-xs mt-1">{myDebts.filter(d => (d.totalDebt || 0) > 0).length} ເຈົ້າໜີ້</p>
          </div>
        )}
      </div>

      {/* Admin quick actions */}
      {isAdmin && (
        <div className="grid grid-cols-3 gap-2">
          {[
            { to: '/debtors', icon: <Users size={22} />, label: 'ລູກໜີ້' },
            { to: '/mydebt', icon: <Wallet size={22} />, label: 'ໜີ້ຂ້ອຍ' },
            { to: '/history', icon: <BarChart2 size={22} />, label: 'ປະຫວັດ' },
          ].map(item => (
            <Link key={item.to} to={item.to}
              className="card flex flex-col items-center gap-1.5 py-3 active:scale-95 transition-all">
              <span className="text-dark">{item.icon}</span>
              <span className="text-xs font-medium text-dark">{item.label}</span>
            </Link>
          ))}
        </div>
      )}

      {/* Debtor list */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h2 className="font-bold text-dark text-sm">ລາຍຊື່ລູກໜີ້</h2>
          <span className="text-xs text-muted">{debtors.length} ລາຍ</span>
        </div>

        {debtors.length === 0 ? (
          <div className="card text-center py-8">
            <div className="flex justify-center mb-2 text-muted/40"><Inbox size={36} /></div>
            <p className="text-muted text-sm">ຍັງບໍ່ມີລູກໜີ້</p>
          </div>
        ) : (
          <div className="space-y-2">
            {debtors.map((d, i) => (
              <Link key={d._id} to={`/debtors/${d._id}`}
                className="card flex items-center justify-between animate-slide-up active:scale-[0.98] transition-all"
                style={{ animationDelay: `${i * 50}ms` }}>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-dark/10 rounded-xl flex items-center justify-center">
                    <span className="text-dark font-bold text-sm">{(d.name || '?')[0]}</span>
                  </div>
                  <div>
                    <p className="font-semibold text-dark text-sm">{d.name}</p>
                    <p className="text-xs text-muted flex items-center gap-1">
                      {(d.totalDebt || 0) <= 0
                        ? <><CheckCircle2 size={12} className="text-green-500" /> ຊຳລະຄົບແລ້ວ</>
                        : 'ຍັງຄ້າງຢູ່'}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <p className={`font-bold text-sm ${(d.totalDebt || 0) > 0 ? 'text-red-500' : 'text-green-500'}`}>
                    {fmt(d.totalDebt)} KIP
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
